import httpClient from "../core/httpClient";

/**
 * @typedef {Object} VoyageDayHoursSummaryRowDto
 * @property {"daily"|"carried_forward"|"total"} row_type
 * @property {string|null} mooring_hours
 * @property {string|null} engine_hours
 * @property {string|null} sailing_hours
 * @property {string|null} nautical_miles
 */

/**
 * @typedef {Object} VoyageDayDto
 * @property {number} id
 * @property {number} voyage_id
 * @property {number[]} log_entries
 * @property {string} date
 * @property {string|null} weekday
 * @property {VoyageDayHoursSummaryRowDto[]|import("./voyageDayHoursSummaryApi").VoyageDayHoursSummaryDto|null} hours_summary
 * @property {boolean} [is_manual]
 * @property {string|null} mooring_hours
 * @property {string|null} engine_hours
 * @property {string|null} sailing_hours
 * @property {string|null} distance_traveled
 * @property {string|null} start_position
 * @property {string|null} stop_position
 * @property {string|null} navigation_lights_off_at
 * @property {string|null} navigation_lights_on_at
 */

function emptyToNull(value) {
	if (value == null) return null;
	const s = String(value).trim();
	return s === "" ? null : s;
}

/**
 * GET `/api/v2/voyages/{id}/days` — dni rejsu w kolejności chronologicznej.
 * @param {number|string} voyageId
 * @returns {Promise<VoyageDayDto[]>}
 */
export async function listVoyageDays(voyageId) {
	const response = await httpClient.get(
		`/api/v2/voyages/${Number(voyageId)}/days`,
	);
	const body = response.data;
	const data = Array.isArray(body?.data) ? body.data : [];
	console.info('[daysApi] listVoyageDays: pobrano', data.length, 'dni dla rejsu', voyageId);
	return data;
}

/**
 * GET `/api/v2/voyages/{id}/days?id_date_only=true` — lista dni rejsu (tylko id i date).
 * @param {number|string} voyageId
 * @returns {Promise<Array<{id: number, date: string}>>}
 */
export async function listVoyageDaysIdDateOnly(voyageId) {
	const response = await httpClient.get(
		`/api/v2/voyages/${Number(voyageId)}/days`,
		{ params: { id_date_only: true } },
	);
	const body = response.data;
	const data = Array.isArray(body?.data) ? body.data : [];
	console.info('[daysApi] listVoyageDaysIdDateOnly: pobrano', data.length, 'dni (id+date) dla rejsu', voyageId);
	return data;
}

/**
 * GET `/api/v2/days/{id}` — szczegóły dnia rejsu.
 * @param {number|string} id
 * @returns {Promise<VoyageDayDto>}
 */
export async function getVoyageDay(id) {
	const response = await httpClient.get(`/api/v2/days/${Number(id)}`);
	const body = response.data;
	if (!body?.id) {
		const err = new Error("Brak danych dnia w odpowiedzi serwera");
		err.response = response;
		throw err;
	}
	console.info('[daysApi] getVoyageDay: pobrano dzien', { id: body?.id, date: body?.date, voyage_id: body?.voyage_id });
	return body;
}

/**
 * POST `/api/v2/days` — utworzenie dnia rejsu.
 * @param {Object} payload
 * @returns {Promise<VoyageDayDto>}
 */
export async function createVoyageDay(payload) {
	const response = await httpClient.post(
		"/api/v2/days",
		buildDayBody(payload),
	);
	const body = response.data;
	if (!body?.id) {
		const err = new Error("Brak danych dnia w odpowiedzi serwera");
		err.response = response;
		throw err;
	}
	console.info('[daysApi] createVoyageDay: utworzono dzien', { id: body?.id, date: body?.date, voyage_id: body?.voyage_id });
	return body;
}

function buildDayBody(payload) {
	return {
		voyage_id: Number(payload.voyage_id),
		date: String(payload.date ?? "").trim(),
		weekday: emptyToNull(payload.weekday),
		start_position: emptyToNull(payload.start_position),
		stop_position: emptyToNull(payload.stop_position),
		navigation_lights_off_at: emptyToNull(payload.navigation_lights_off_at),
		navigation_lights_on_at: emptyToNull(payload.navigation_lights_on_at),
	};
}

/**
 * PUT `/api/v2/days/{id}` — aktualizacja dnia rejsu.
 * @param {number|string} id
 * @param {Object} payload
 * @returns {Promise<VoyageDayDto>}
 */
export async function updateVoyageDay(id, payload) {
	const response = await httpClient.put(
		`/api/v2/days/${Number(id)}`,
		buildDayBody(payload),
	);
	const body = response.data;
	if (!body?.id) {
		const err = new Error("Brak danych dnia w odpowiedzi serwera");
		err.response = response;
		throw err;
	}
	console.info('[daysApi] updateVoyageDay: zaktualizowano dzien', { id: body?.id, date: body?.date });
	return body;
}

/**
 * DELETE `/api/v2/days/{id}` — usunięcie dnia rejsu.
 * @param {number|string} id
 */
export async function deleteVoyageDay(id) {
	const response = await httpClient.delete(`/api/v2/days/${Number(id)}`);
	const body = response.data;
	console.info('[daysApi] deleteVoyageDay:', body?.message);
}

/** API → wartość `datetime-local`. */
export function apiDateTimeToDatetimeLocal(value) {
	if (value == null || value === "") return "";
	const normalized = String(value).replace(" ", "T");
	const d = new Date(normalized);
	if (Number.isNaN(d.getTime())) return "";
	const pad = (n) => String(n).padStart(2, "0");
	return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

/** `datetime-local` → ISO z offsetem lokalnym. */
export function datetimeLocalToApiIso(value) {
	if (value == null || value === "") return null;
	const d = new Date(value);
	if (Number.isNaN(d.getTime())) return null;
	const pad = (n) => String(n).padStart(2, "0");
	const off = -d.getTimezoneOffset();
	const sign = off >= 0 ? "+" : "-";
	const abs = Math.abs(off);
	return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}:00${sign}${pad(Math.floor(abs / 60))}:${pad(abs % 60)}`;
}

/** DTO → stan formularza edycji. */
export function voyageDayToFormState(day, voyageId) {
	if (!day) {
		return {
			voyage_id: Number(voyageId),
			date: "",
			weekday: "",
			mooring_hours: "",
			engine_hours: "",
			sailing_hours: "",
			distance_traveled: "",
			start_position: "",
			stop_position: "",
			navigation_lights_on_at: "",
			navigation_lights_off_at: "",
			navigation_lights_on_local: "",
			navigation_lights_off_local: "",
		};
	}
	return {
		voyage_id: day.voyage_id ?? Number(voyageId),
		date: day.date ?? "",
		weekday: day.weekday ?? weekdayFromIsoDate(day.date ?? ""),
		mooring_hours: day.mooring_hours ?? "",
		engine_hours: day.engine_hours ?? "",
		sailing_hours: day.sailing_hours ?? "",
		distance_traveled: day.distance_traveled ?? "",
		start_position: day.start_position ?? "",
		stop_position: day.stop_position ?? "",
		navigation_lights_on_at: day.navigation_lights_on_at ?? "",
		navigation_lights_off_at: day.navigation_lights_off_at ?? "",
		navigation_lights_on_local: apiDateTimeToDatetimeLocal(
			day.navigation_lights_on_at,
		),
		navigation_lights_off_local: apiDateTimeToDatetimeLocal(
			day.navigation_lights_off_at,
		),
	};
}

/** Stan formularza → payload PUT/POST. */
export function formStateToDayPayload(form, voyageId) {
	return {
		voyage_id: form.voyage_id ?? Number(voyageId),
		date: form.date,
		weekday: form.weekday,
		mooring_hours: form.mooring_hours,
		engine_hours: form.engine_hours,
		sailing_hours: form.sailing_hours,
		distance_traveled: form.distance_traveled,
		start_position: form.start_position,
		stop_position: form.stop_position,
		navigation_lights_on_at: datetimeLocalToApiIso(
			form.navigation_lights_on_local,
		),
		navigation_lights_off_at: datetimeLocalToApiIso(
			form.navigation_lights_off_local,
		),
	};
}

/** `YYYY-MM-DD` → `DD.MM.YYYY` (wyświetlanie). */
export function formatVoyageDayDate(value) {
	if (value == null || value === "") return "—";
	const parts = String(value).split("-");
	if (parts.length === 3) {
		const [y, m, d] = parts;
		return `${d.padStart(2, "0")}.${m.padStart(2, "0")}.${y}`;
	}
	try {
		const d = new Date(String(value).replace(" ", "T"));
		if (!Number.isNaN(d.getTime())) {
			return d.toLocaleDateString("pl-PL");
		}
	} catch {
		// fall through
	}
	return String(value);
}

/** ISO lub `YYYY-MM-DD HH:mm:ss` → tekst lokalny. */
export function formatVoyageDayDateTime(value) {
	if (value == null || value === "") return "—";
	try {
		const normalized = String(value).replace(" ", "T");
		const d = new Date(normalized);
		if (Number.isNaN(d.getTime())) return String(value);
		return d.toLocaleString("pl-PL", {
			year: "numeric",
			month: "2-digit",
			day: "2-digit",
			hour: "2-digit",
			minute: "2-digit",
		});
	} catch {
		return String(value);
	}
}

export function displayNullable(value) {
	if (value == null || value === "") return "—";
	return String(value);
}

/** Następna data ISO (YYYY-MM-DD) po ostatnim dniu lub dziś. */
export function suggestNextVoyageDayDate(days) {
	const list = Array.isArray(days) ? days : [];
	if (list.length === 0) {
		const today = new Date();
		return toIsoDate(today);
	}
	const last = list[list.length - 1];
	const base = last?.date ? new Date(`${last.date}T12:00:00`) : new Date();
	if (Number.isNaN(base.getTime())) return toIsoDate(new Date());
	base.setDate(base.getDate() + 1);
	return toIsoDate(base);
}

function toIsoDate(d) {
	const pad = (n) => String(n).padStart(2, "0");
	return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
}

/** `YYYY-MM-DD` → polska nazwa dnia tygodnia (np. „Poniedziałek”). */
export function weekdayFromIsoDate(value) {
	if (value == null || value === "") return "";
	const raw = String(value).trim();
	const d = new Date(`${raw}T12:00:00`);
	if (Number.isNaN(d.getTime())) return "";
	const name = d.toLocaleDateString("pl-PL", { weekday: "long" });
	return name ? name.charAt(0).toUpperCase() + name.slice(1) : "";
}

export const DAY_HEADER_FIELD_KEYS = [
	"date",
	"weekday",
	"start_position",
	"stop_position",
	"navigation_lights_on_local",
	"navigation_lights_off_local",
];

export function dayHeaderFormSlice(form) {
	return Object.fromEntries(
		DAY_HEADER_FIELD_KEYS.map((key) => [key, form?.[key] ?? ""]),
	);
}

export function isDayHeaderFormDirty(form, day, voyageId) {
	const baseline = dayHeaderFormSlice(voyageDayToFormState(day, voyageId));
	const current = dayHeaderFormSlice(form);
	return JSON.stringify(current) !== JSON.stringify(baseline);
}
